
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface UserPlanInfo {
  plan: 'GRATIS' | 'PRO';
  is_grandfathered: boolean;
  is_pro: boolean;
  ai_usage: number;
  ai_limit: number;
  max_projects: number;
  max_users: number;
}

const DEFAULT_FREE: UserPlanInfo = {
  plan: 'GRATIS',
  is_grandfathered: false,
  is_pro: false,
  ai_usage: 0,
  ai_limit: 10,
  max_projects: 2,
  max_users: 5,
};

export const useUserPlan = () => {
  const { user } = useAuth();
  const [planInfo, setPlanInfo] = useState<UserPlanInfo>(DEFAULT_FREE);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    if (!user) {
      setPlanInfo(DEFAULT_FREE);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_user_plan_info', {
        p_user_id: user.id,
      });

      if (error) {
        console.error('Error fetching plan info:', error);
        setPlanInfo(DEFAULT_FREE);
      } else {
        setPlanInfo(data as unknown as UserPlanInfo);
      }
    } catch (err) {
      console.error('Error fetching plan info:', err);
      setPlanInfo(DEFAULT_FREE);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const checkAIUsage = useCallback(async (): Promise<{ allowed: boolean; count: number; limit: number }> => {
    if (!user) return { allowed: false, count: 0, limit: 0 };

    try {
      const { data, error } = await supabase.rpc('increment_ai_usage', {
        p_user_id: user.id,
      });

      if (error) {
        console.error('Error checking AI usage:', error);
        return { allowed: true, count: 0, limit: -1 }; // fail open
      }

      const result = data as any;
      return {
        allowed: result.allowed,
        count: result.count,
        limit: result.limit,
      };
    } catch (err) {
      console.error('Error checking AI usage:', err);
      return { allowed: true, count: 0, limit: -1 };
    }
  }, [user]);

  const canAccessFeature = useCallback((feature: string): boolean => {
    if (planInfo.is_pro || planInfo.is_grandfathered) return true;
    
    // Features available on the free plan
    const freeFeatures = [
      'kanban', 'dashboard_basic', 'reports_basic', 'calendar', 'templates',
      'projects', 'virtual_agent', 'team', 'settings', 'task_track',
    ];
    return freeFeatures.includes(feature);
  }, [planInfo]);

  return {
    planInfo,
    loading,
    checkAIUsage,
    canAccessFeature,
    refreshPlan: fetchPlan,
    isPro: planInfo.is_pro || planInfo.is_grandfathered,
  };
};
